import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { MutationProcessingResponse, MutationProcessingSetting, MutationProcessingUpdatePayload } from "../types/mutationProcessing.types";
import { MutationProcessingRow } from "./MutationProcessingRow";

const groupFor = (type: string) => {
  if (type.startsWith("CHILD_")) return "children";
  if (type.startsWith("PARENT_")) return "parents";
  if (type.startsWith("FAMILY_") || type === "PARENTAL_AUTHORITY_CHANGED" || type === "NEW_PARTNER") return "family";
  if (type.startsWith("BANK_ACCOUNT_")) return "finance";
  return "other";
};

interface MutationProcessingListProps {
  settings: MutationProcessingResponse;
  canUpdate: boolean;
  savingType: string | null;
  onUpdate: (setting: MutationProcessingSetting, payload: MutationProcessingUpdatePayload) => void;
}

export const MutationProcessingList = ({ settings, canUpdate, savingType, onUpdate }: MutationProcessingListProps) => {
  const { t } = useTranslation();
  const grouped = useMemo(() => {
    return settings.reduce<Record<string, MutationProcessingSetting[]>>((acc, setting) => {
      const group = groupFor(setting.mutationType);
      acc[group] = [...(acc[group] || []), setting];
      return acc;
    }, {});
  }, [settings]);

  if (settings.length === 0) {
    return (
      <div className="lumen-plain-card rounded-2xl p-6 text-center text-sm font-medium text-slate-600 dark:text-slate-300">
        {t("admin.mutation_processing.empty", { defaultValue: "No mutation processing settings are available." })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {(Object.entries(grouped) as [string, MutationProcessingSetting[]][]).map(([group, items]) => (
        <section key={group} className="space-y-3">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t(`admin.mutation_processing.groups.${group}`, { defaultValue: group })}
          </h2>
          <div className="grid gap-3">
            {items.map((setting, index) => (
              <MutationProcessingRow
                key={setting.mutationType}
                setting={setting}
                canUpdate={canUpdate}
                saving={savingType === setting.mutationType}
                onUpdate={onUpdate}
                animationDelayMs={index * 45}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
